import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from '../icons';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { fetchLocationByQuery } from '../../store/slices/jobs/jobSlice';

interface SearchDropdownProps {
    items: Array<{
        id: number;
        name: string;
        [key: string]: any;
    }>;
    buttonText: string;
    selectedItem: any;
    onChange: (item: any) => void;
    disabled?: boolean;
    isLoading?: boolean;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({ 
    items = [], 
    buttonText, 
    selectedItem, 
    onChange,
    disabled = false,
    isLoading = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isSearching, setIsSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const searchTimeoutRef = useRef<NodeJS.Timeout>();

    const safeItems = Array.isArray(items) ? items.filter(item => 
        item && typeof item === 'object' && 'locationName' in item && typeof item.locationName === 'string'
    ) : [];
    
    const toggleDropdown = () => {
        if (!disabled && !isLoading) {
            setIsOpen(!isOpen);
        }
    };

    const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value.toLowerCase();
        setSearchTerm(searchTerm);
        setSelectedIndex(-1);
        
        // Set searching state immediately
        setIsSearching(true);
        
        // Clear any existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // Debounce the API call
        searchTimeoutRef.current = setTimeout(async () => {
            if (searchTerm.trim()) {
                try {
                    await dispatch(fetchLocationByQuery(searchTerm));
                } finally {
                    setIsSearching(false);
                }
            } else {
                setIsSearching(false);
            }
        }, 300);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!isOpen) return;
    
        const filteredItems = safeItems.filter(item => 
            item.locationName.toLowerCase().includes(searchTerm)
        );
    
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setSelectedIndex((prevIndex) => {
                    const newIndex = Math.min(prevIndex + 1, filteredItems.length - 1);
                    scrollIntoView(newIndex);
                    return newIndex;
                });
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                setSelectedIndex((prevIndex) => {
                    const newIndex = Math.max(prevIndex - 1, 0);
                    scrollIntoView(newIndex);
                    return newIndex;
                });
                break;
                
            case 'Enter':
                if (selectedIndex >= 0) {
                    event.preventDefault();
                    handleItemClick(filteredItems[selectedIndex]);
                }
                break;
                
            case 'Escape':
                setIsOpen(false);
                break;
        }
    };

    const scrollIntoView = (index: number) => {
        if (listRef.current) {
            const selectedElement = listRef.current.children[index];
            if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    };

    const handleItemClick = (item: any) => {
        setSearchTerm('');
        setIsOpen(false);
        onChange(item);
    };

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Focus input when dropdown opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const filteredItems = safeItems.filter(item =>
        item.locationName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex items-center justify-center w-full">
            <div className="relative group w-full">
                {!isOpen && (
                    <button
                        onClick={toggleDropdown}
                        disabled={disabled || isLoading}
                        className={`inline-flex justify-between items-center w-full pl-3 pr-2 py-2 h-[42px] text-sm font-medium text-gray-700 bg-white border
                            border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 button-hover-location
                            ${(disabled || isLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <span className="mr-2 font-normal text-[14px]">
                            {selectedItem ? selectedItem.locationName : <span className='text-[#9ca3af]'>{buttonText}</span>}
                        </span>
                        {isLoading ? (
                            <div className="simple-spinner-loader">
                                <span className="!w-4 !h-4"></span>
                            </div>
                        ) : (
                            <ChevronDownIcon />
                        )}
                    </button>
                )}
                
                {isOpen && (
                    <div className="relative">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search country"
                            autoComplete="off"
                            value={searchTerm}
                            onChange={handleSearch}
                            onKeyDown={handleKeyDown}
                            className="block w-full px-4 py-2 text-gray-800 border rounded-md border-gray-300 focus:outline-none input_hover !outline-offset-[-2px]"
                        />
                        {isSearching && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="simple-spinner-loader">
                                    <span className="!w-4 !h-4"></span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {isOpen && !isSearching && filteredItems.length > 0 && (
                    <div className="w-full z-10 absolute right-0 mt-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 p-1 space-y-1">
                        <div ref={listRef} className="overflow-y-auto max-h-32">
                            {filteredItems.map((item, index) => (
                                <a
                                    key={item.id}
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleItemClick(item);
                                    }}
                                    className={`block px-4 py-2 text-gray-700 hover:bg-gray-100 active:bg-indigo-100 cursor-pointer rounded-md ${
                                        selectedIndex === index ? 'bg-indigo-100' : ''
                                    }`}
                                >
                                    {item.locationName}
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchDropdown;